import {
  ArrowLeft,
  Eye,
  Image as ImageIcon,
  Save,
  Upload,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { useLanguage } from "../../context/LanguageContext";
import { postsAPI } from "../../lib/api";
import AdminLayout from "./AdminLayout";

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const isNew = id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("ro");
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    title_ro: "",
    title_en: "",
    title_ar: "",
    content_ro: "",
    content_en: "",
    content_ar: "",
    excerpt_ro: "",
    excerpt_en: "",
    excerpt_ar: "",
    category: "announcement",
    cover_image: "",
    status: "draft",
  });

  const labels = {
    ro: {
      settings: "Setări",
      status: "Status",
      category: "Categorie",
      coverImage: "Imagine de copertă",
      uploadImage: "Încarcă imagine",
      removeImage: "Șterge",
      orPasteUrl: "sau lipește URL",
      imageUrl: "URL imagine",
    },
    en: {
      settings: "Settings",
      status: "Status",
      category: "Category",
      coverImage: "Cover Image",
      uploadImage: "Upload Image",
      removeImage: "Remove",
      orPasteUrl: "or paste URL",
      imageUrl: "Image URL",
    },
    ar: {
      settings: "الإعدادات",
      status: "الحالة",
      category: "الفئة",
      coverImage: "صورة الغلاف",
      uploadImage: "رفع صورة",
      removeImage: "حذف",
      orPasteUrl: "أو الصق رابط",
      imageUrl: "رابط الصورة",
    },
  };

  const l = labels[lang] || labels.ro;

  const fetchPost = useCallback(async () => {
    try {
      const data = await postsAPI.getById(id);
      setFormData({
        title_ro: data.title_ro || "",
        title_en: data.title_en || "",
        title_ar: data.title_ar || "",
        content_ro: data.content_ro || "",
        content_en: data.content_en || "",
        content_ar: data.content_ar || "",
        excerpt_ro: data.excerpt_ro || "",
        excerpt_en: data.excerpt_en || "",
        excerpt_ar: data.excerpt_ar || "",
        category: data.category || "announcement",
        cover_image: data.cover_image || "",
        status: data.status || "draft",
      });
      if (data.cover_image) {
        setImagePreview(data.cover_image);
      }
    } catch (error) {
      toast.error("Failed to fetch post");
      navigate("/admin/posts");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!isNew) {
      fetchPost();
    }
  }, [fetchPost, isNew]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(
          lang === "en"
            ? "Image too large (max 5MB)"
            : "Imagine prea mare (max 5MB)",
        );
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        // For now, store as data URL (will be replaced by Cloudinary upload)
        handleChange("cover_image", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    handleChange("cover_image", "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title_ro && !formData.title_en) {
      toast.error(
        lang === "en"
          ? "Please enter at least one title"
          : "Vă rugăm să introduceți cel puțin un titlu",
      );
      return;
    }

    setSaving(true);

    try {
      if (isNew) {
        await postsAPI.create(formData);
        toast.success(lang === "en" ? "Post created" : "Anunț creat");
      } else {
        await postsAPI.update(id, formData);
        toast.success(lang === "en" ? "Post updated" : "Anunț actualizat");
      }
      navigate("/admin/posts");
    } catch (error) {
      toast.error(isNew ? "Failed to create post" : "Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-stone-200 rounded w-1/4"></div>
          <div className="h-96 bg-stone-200 rounded"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/posts")}
            data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-stone-800">
              {isNew ? t("admin.createPost") : t("admin.editPost")}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <a
              href={`/annonces/${id}`}
              target="_blank"
              rel="noopener noreferrer">
              <Button variant="outline" className="gap-2">
                <Eye className="w-4 h-4" />
                {t("common.view")}
              </Button>
            </a>
          )}
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-islamic-teal hover:bg-islamic-teal-light gap-2"
            data-testid="save-post-btn">
            <Save className="w-4 h-4" />
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="ro">Română</TabsTrigger>
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="ar">العربية</TabsTrigger>
                  </TabsList>

                  <TabsContent value="ro" className="space-y-4">
                    <div>
                      <Label htmlFor="title_ro">Titlu (RO) *</Label>
                      <Input
                        id="title_ro"
                        value={formData.title_ro}
                        onChange={(e) =>
                          handleChange("title_ro", e.target.value)
                        }
                        placeholder="Titlul anunțului"
                        data-testid="title-ro"
                      />
                    </div>
                    <div>
                      <Label htmlFor="excerpt_ro">Rezumat (RO)</Label>
                      <Textarea
                        id="excerpt_ro"
                        value={formData.excerpt_ro}
                        onChange={(e) =>
                          handleChange("excerpt_ro", e.target.value)
                        }
                        placeholder="Un scurt rezumat..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="content_ro">Conținut (RO) *</Label>
                      <Textarea
                        id="content_ro"
                        value={formData.content_ro}
                        onChange={(e) =>
                          handleChange("content_ro", e.target.value)
                        }
                        placeholder="Conținutul complet al anunțului..."
                        rows={12}
                        data-testid="content-ro"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="en" className="space-y-4">
                    <div>
                      <Label htmlFor="title_en">Title (EN)</Label>
                      <Input
                        id="title_en"
                        value={formData.title_en}
                        onChange={(e) =>
                          handleChange("title_en", e.target.value)
                        }
                        placeholder="Announcement title"
                        data-testid="title-en"
                      />
                    </div>
                    <div>
                      <Label htmlFor="excerpt_en">Excerpt (EN)</Label>
                      <Textarea
                        id="excerpt_en"
                        value={formData.excerpt_en}
                        onChange={(e) =>
                          handleChange("excerpt_en", e.target.value)
                        }
                        placeholder="A short summary..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="content_en">Content (EN)</Label>
                      <Textarea
                        id="content_en"
                        value={formData.content_en}
                        onChange={(e) =>
                          handleChange("content_en", e.target.value)
                        }
                        placeholder="Full announcement content..."
                        rows={12}
                        data-testid="content-en"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="ar" className="space-y-4">
                    <div>
                      <Label htmlFor="title_ar">العنوان (AR)</Label>
                      <Input
                        id="title_ar"
                        value={formData.title_ar}
                        onChange={(e) =>
                          handleChange("title_ar", e.target.value)
                        }
                        placeholder="عنوان الإعلان"
                        className="text-right font-arabic"
                        dir="rtl"
                        data-testid="title-ar"
                      />
                    </div>
                    <div>
                      <Label htmlFor="excerpt_ar">الملخص (AR)</Label>
                      <Textarea
                        id="excerpt_ar"
                        value={formData.excerpt_ar}
                        onChange={(e) =>
                          handleChange("excerpt_ar", e.target.value)
                        }
                        placeholder="ملخص قصير..."
                        rows={2}
                        className="text-right font-arabic"
                        dir="rtl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="content_ar">المحتوى (AR)</Label>
                      <Textarea
                        id="content_ar"
                        value={formData.content_ar}
                        onChange={(e) =>
                          handleChange("content_ar", e.target.value)
                        }
                        placeholder="المحتوى الكامل للإعلان..."
                        rows={12}
                        className="text-right font-arabic"
                        dir="rtl"
                        data-testid="content-ar"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-stone-200">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold text-stone-800">{l.settings}</h3>

                <div>
                  <Label htmlFor="status">{l.status}</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange("status", value)}>
                    <SelectTrigger id="status" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">
                        {t("admin.status.draft")}
                      </SelectItem>
                      <SelectItem value="published">
                        {t("admin.status.published")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="category">{l.category}</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleChange("category", value)}>
                    <SelectTrigger id="category" data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="announcement">Announcement</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="news">News</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Cover Image Upload */}
                <div>
                  <Label>{l.coverImage}</Label>

                  {imagePreview ? (
                    <div className="mt-2 relative">
                      <img
                        src={imagePreview}
                        alt="Cover preview"
                        className="w-full h-40 object-cover rounded-lg border border-stone-200"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8"
                        onClick={removeImage}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-stone-300 hover:border-islamic-teal hover:bg-stone-50">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="w-8 h-8 text-stone-400 mb-2" />
                          <p className="text-sm text-stone-500">
                            {l.uploadImage}
                          </p>
                          <p className="text-xs text-stone-400">
                            PNG, JPG (max 5MB)
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={handleImageUpload}
                          data-testid="image-upload"
                        />
                      </label>
                    </div>
                  )}

                  <div className="mt-3">
                    <p className="text-xs text-stone-500 mb-1">
                      {l.orPasteUrl}
                    </p>
                    <Input
                      value={
                        formData.cover_image.startsWith("data:")
                          ? ""
                          : formData.cover_image
                      }
                      onChange={(e) => {
                        handleChange("cover_image", e.target.value);
                        setImagePreview(e.target.value || null);
                      }}
                      placeholder="https://..."
                      data-testid="cover-image-url"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default AdminPostEditor;
